import { Component, OnInit, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BreadcrumbService, BreadcrumbItem } from '../../shared/services/breadcrumb.service';
import { SubscriptionsManager } from '../../shared/helpers/subscriptions-manager';

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss']
})
export class BreadcrumbComponent implements OnInit, OnChanges, OnDestroy {
  @Input() customBreadcrumbs?: BreadcrumbItem[];

  breadcrumbs: BreadcrumbItem[] = [];
  private subsManager = new SubscriptionsManager();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private breadcrumbService: BreadcrumbService
  ) { }

  ngOnInit() {
    // Subscribe to router events for automatic breadcrumb detection
    this.subsManager.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => {
          this.buildBreadcrumbs();
        })
    );

    this.subsManager.add(
      this.breadcrumbService.breadcrumbs$.subscribe(customBreadcrumbs => {
        if (customBreadcrumbs && customBreadcrumbs.length > 0) {
          this.buildCustomBreadcrumbs();
        } else {
          this.buildBreadcrumbs();
        }
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['customBreadcrumbs'] && this.customBreadcrumbs) {
      this.buildCustomBreadcrumbs();
    }
  }

  ngOnDestroy() {
    this.subsManager.unsubscribeAll();
  }

  private buildBreadcrumbs() {
    if (this.customBreadcrumbs) {
      this.buildCustomBreadcrumbs();
      return;
    }

    this.breadcrumbs = [];

    this.breadcrumbs.push({
      label: 'Home',
      url: '/',
      isActive: false
    });

    const urlSegments = this.router.url.split('/').filter(segment => segment);

    let currentUrl = '';
    urlSegments.forEach((segment, index) => {
      currentUrl += `/${segment}`;

      // Convert segment to readable label
      const label = this.formatSegment(segment);

      this.breadcrumbs.push({
        label: label,
        url: currentUrl,
        isActive: index === urlSegments.length - 1
      });
    });

    this.breadcrumbs.forEach((breadcrumb, index) => {
      breadcrumb.isActive = index === this.breadcrumbs.length - 1;
    });
  }

  private buildCustomBreadcrumbs() {
    const customBreadcrumbs = this.customBreadcrumbs || this.breadcrumbService.getCurrentBreadcrumbs() || [];
    if (!customBreadcrumbs || customBreadcrumbs.length === 0) return;

    this.breadcrumbs = [];

    this.breadcrumbs.push({
      label: 'Home',
      url: '/',
      isActive: false
    });

    // Add custom breadcrumbs
    customBreadcrumbs.forEach((breadcrumb: BreadcrumbItem, index: number) => {
      this.breadcrumbs.push({
        ...breadcrumb,
        isActive: index === customBreadcrumbs.length - 1
      });
    });
  }

  private formatSegment(segment: string): string {

    const segmentMap: { [key: string]: string; } = {
      'boards': 'Boards',
      'dashboard': 'Dashboard',
      'congress-calendar': 'Congress Calendar',
      'work-area': 'Work Area',
      'boards-manager': 'Boards Manager',
      'ai-chat': 'AI Chat',
      'article-card': 'Article Card',
      'board-members': 'Board Members',
      'search-article': 'Search Article',
      'tags-manager': 'Tags Manager',
      'month-view': 'Month View',
      'month-activity-view': 'Month Activity View',
      'news-feed': 'News Feed',
      'stats-boxes': 'Statistics',
      'doc-types-stats': 'Document Types',
      'relevant-keys-stats': 'Relevant Keys',
      'today-schedule': 'Today Schedule',
      'feed-search-bar': 'Search Bar'
    };

    if (segmentMap[segment]) {
      return segmentMap[segment];
    }

    return segment
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  }

  navigateTo(url: string) {
    this.router.navigate([url]);
  }
}
